import type { SeededRandom } from '../../seeded-random';
import type { GenerateFillLayoutOptions, MapGenTileCoord, TileLayoutResult } from '../tile-layout-types';

import type { FillLayoutContext } from './fill-layout-context';
import { createFillLayoutContext, topUpAndFinalize } from './fill-layout-context';
import {
  canCommitCells,
  cellsFitWithForeignGap,
  commitCells,
  DIRECTIONS_8,
  tileKey,
  turnDirection
} from './wall-line-shared';

/** Random diagonal-biased wall lines with open-gap spacing; tops up with scatter afterward. */
export function generateDiagonalBiasedWallsLayout(options: GenerateFillLayoutOptions): TileLayoutResult {
  const ctx = createFillLayoutContext(options);
  const primaryFilled = placeDiagonalBiasedWalls(ctx);
  return topUpAndFinalize(ctx, 'diagonalBiasedWalls', primaryFilled);
}

export function placeDiagonalBiasedWalls(ctx: FillLayoutContext): number {
  const { grid, width, height, budget, gapTiles, minPlacementSizeTiles, maxPlacementSizeTiles, fillTileValue, rand } = ctx;
  const wallCount = Math.max(10, Math.floor((width * height * ctx.coverageCap) / 80));
  let filled = 0;

  for (let i = 0; i < wallCount; i++) {
    const complexityBias = 1 - (i / Math.max(1, wallCount));
    const turnChance = 0.18 + (0.28 * complexityBias);

    for (let length = maxPlacementSizeTiles; length >= minPlacementSizeTiles; length--) {
      const cells = planAngledWallLine(width, height, length, turnChance, rand);
      if (cells.length < minPlacementSizeTiles) {
        continue;
      }

      if (!cellsFitWithForeignGap(grid, width, height, cells, gapTiles, fillTileValue)) {
        continue;
      }

      if (!canCommitCells(grid, width, height, cells, budget, fillTileValue)) {
        continue;
      }
      filled += commitCells(grid, width, height, cells, fillTileValue);
      break;
    }
  }

  return filled;
}

function planAngledWallLine(
  width: number,
  height: number,
  targetLength: number,
  turnChance: number,
  rand: SeededRandom
): MapGenTileCoord[] {
  let col = Math.floor(rand() * width);
  let row = Math.floor(rand() * height);
  let dir = DIRECTIONS_8[Math.floor(rand() * DIRECTIONS_8.length)];
  const cells: MapGenTileCoord[] = [];
  const seen = new Set<string>();

  for (let step = 0; step < targetLength; step++) {
    if (col < 0 || row < 0 || col >= width || row >= height) {
      break;
    }

    const key = tileKey(col, row);
    if (!seen.has(key)) {
      cells.push({ col, row });
      seen.add(key);
    }

    if (step > 2 && rand() < turnChance) {
      dir = turnDirection(dir, DIRECTIONS_8, rand);
    }

    col += dir.dc;
    row += dir.dr;
  }

  return cells;
}
